import React from "react";
import { useTranslation } from "react-i18next";
import { Check, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { ProfileAvatar } from "@/components/common/ProfileAvatar";
import { MessageActionsPopover } from "@/components/common/MessageActionsPopover";
import { formatMessageTime, getParticipantName, type MessageGroup } from "./utils";
import type { InboxConversation, InboxParticipant, MatchedCandidate, SaveEntity } from "@/types/inbox";

interface ChatMessageGroupProps {
	group: MessageGroup;
	conversation: InboxConversation;
	participantsById: Record<string, InboxParticipant>;
	matchedCandidate?: MatchedCandidate | null;
	/** Host entity to pre-link saves to; falls back to the matched candidate. */
	currentEntity?: SaveEntity;
}

/**
 * A run of consecutive chat messages from one sender. Outgoing messages use
 * the primary token, incoming ones the muted surface — no more hardcoded
 * green/blue bubbles.
 */
export const ChatMessageGroup: React.FC<ChatMessageGroupProps> = ({
	group,
	conversation,
	participantsById,
	matchedCandidate,
	currentEntity,
}) => {
	const { t, i18n } = useTranslation();
	const outgoing = group.isSender;
	const participant = participantsById[group.senderAttendeeId];
	const senderName = outgoing
		? t("inbox.you", "You")
		: getParticipantName(participant) || group.sender?.name || t("inbox.unknownSender", "Unknown");

	return (
		<div className={cn("flex w-full gap-2.5", outgoing ? "justify-end" : "justify-start")}>
			{!outgoing && (
				<ProfileAvatar
					src={participant?.picture_url}
					alt={senderName}
					className="mt-auto h-7 w-7 shrink-0"
					participantId={participant?.id}
					conversationId={conversation.id}
				/>
			)}
			<div className={cn("flex max-w-[75%] flex-col gap-0.5", outgoing && "items-end")}>
				{!outgoing && (
					<span className="mb-0.5 px-1 text-xs font-medium text-muted-foreground">{senderName}</span>
				)}
				{group.messages.map((message, index) => {
					const isLast = index === group.messages.length - 1;
					return (
						<div key={message.id} className={cn("group flex items-center gap-1.5", outgoing && "flex-row-reverse")}>
							<div
								className={cn(
									"whitespace-pre-wrap break-words rounded-md px-3 py-2 text-sm shadow-sm animate-fade-in",
									outgoing
										? "bg-primary text-primary-foreground"
										: "bg-muted text-foreground",
									message.optimistic && "opacity-70"
								)}
							>
								{message.text}
							</div>
							{!message.optimistic && (
								<MessageActionsPopover
									messageData={{
										id: message.id,
										text: message.text || "",
										subject: conversation.subject,
										messageType: conversation.conversation_type,
										participants: conversation.participants,
									}}
									currentEntity={
										currentEntity ??
										(matchedCandidate
											? { type: "candidates", id: matchedCandidate.id, name: matchedCandidate.name }
											: undefined)
									}
								/>
							)}
							{isLast && (
								<span className="sr-only">{formatMessageTime(message.timestamp, i18n.language)}</span>
							)}
						</div>
					);
				})}
				<span
					className={cn(
						"mt-0.5 flex items-center gap-1 px-1 text-[11px] tabular-nums text-muted-foreground",
						outgoing && "justify-end"
					)}
				>
					{formatMessageTime(group.timestamp, i18n.language)}
					{outgoing && group.messages.some((m) => m.sending) && (
						<Clock className="h-3 w-3" aria-label={t("inbox.sending", "Sending…")} />
					)}
					{outgoing && !group.messages.some((m) => m.sending) && group.messages.some((m) => m.sent) && (
						<Check className="h-3 w-3" aria-label={t("inbox.sent", "Sent")} />
					)}
				</span>
			</div>
		</div>
	);
};
